// src/components/Layout/RoleList.js
import React, { useEffect, useState, useContext } from 'react';
import { getRoles, deleteRol } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedName, setSelectedName] = useState(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await getRoles();
        if (data.success) {
          setRoles(data.data);
        } else {
          setError('Error al obtener roles');
        }
      } catch (err) {
        setError(err.error || 'Error al obtener roles');
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleEdit = (nombre) => {
    navigate(`/roles/edit/${nombre}`);
  };

  const handleDeleteClick = (nombre) => {
    setSelectedName(nombre);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const data = await deleteRol(selectedName);
      if (data.success) {
        setRoles(roles.filter((rol) => rol.nombre !== selectedName));
      } else {
        setError('Error al eliminar rol');
      }
    } catch (err) {
      setError(err.error || 'Error al eliminar rol');
    } finally {
      setOpenDialog(false);
      setSelectedName(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSelectedName(null);
  };

  if (loading) return <CircularProgress />;

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={2}>
        <Typography variant="h4">Roles y Permisos</Typography>
        {user.permisos && user.permisos.crearUsuarios && (
          <Button variant="contained" color="primary" onClick={() => navigate('/roles/new')}>
            Crear Rol
          </Button>
        )}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nombre del Rol</TableCell>
            <TableCell>Crear Productos</TableCell>
            <TableCell>Modificar Precios</TableCell>
            <TableCell>Ver Reportes</TableCell>
            <TableCell>Crear Usuarios</TableCell>
            <TableCell>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map((rol) => (
            <TableRow key={rol.nombre}>
              <TableCell>{rol.nombre}</TableCell>
              <TableCell>{rol.crearProductos ? 'Sí' : 'No'}</TableCell>
              <TableCell>{rol.modificarPrecios ? 'Sí' : 'No'}</TableCell>
              <TableCell>{rol.verReportes ? 'Sí' : 'No'}</TableCell>
              <TableCell>{rol.crearUsuarios ? 'Sí' : 'No'}</TableCell>
              <TableCell>
                <Button variant="outlined" color="primary" onClick={() => handleEdit(rol.nombre)}>
                  Editar
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleDeleteClick(rol.nombre)}
                  style={{ marginLeft: '8px' }}
                >
                  Eliminar
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Diálogo de Confirmación para Eliminar */}
      <Dialog
        open={openDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar este rol? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RoleList;
